import { createGlobalStyle } from "styled-components";

const GlobalStyles = createGlobalStyle`
  :root {
    /* Core Gourmeto tokens */
    --color-ink: #0f0f10;
    --color-ink-solid: #0f0f10;
    --color-cream: #faf7f1;
    --color-yellow: #ffc629;
    --color-sage: #8b9b7a;
    --color-stone: #d9d5cc;
    --surface-dark: #171717;
    --surface-dark-2: #222222;
    --surface-light: #ffffff;
    --text-primary: #0f0f10;
    --text-secondary: #6b6560;
    --text-on-dark: #f7f3ec;
    --text-on-dark-muted: #cfc8be;
    --border-subtle: rgba(15, 15, 16, 0.08);
    --border-on-dark: rgba(247, 243, 236, 0.12);

    /* Spacing */
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 24px;
    --space-6: 32px;
    --space-7: 48px;
    --space-8: 64px;

    /* Radius */
    --radius-sm: 10px;
    --radius: 16px;
    --radius-lg: 22px;
    --radius-xl: 28px;
    --radius-pill: 999px;

    /* Shadow — restrained, editorial */
    --shadow-soft: 0 8px 24px rgba(15, 15, 16, 0.05);
    --shadow-none: none;

    /* Type */
    --font-display: "Playfair Display", "Fraunces", Georgia, serif;
    --font-body: "Inter", "Outfit", "Segoe UI", sans-serif;
    --font-size-xs: 11px;
    --font-size-sm: 13px;
    --font-size-md: 15px;
    --font-size-lg: 18px;
    --font-size-xl: 24px;
    --font-size-hero: clamp(36px, 6vw, 64px);

    /* Motion */
    --ease: cubic-bezier(0.22, 1, 0.36, 1);
    --duration: 0.18s;

    /* Chrome */
    --tabbar-h: 68px;
    --header-h: 58px;
    --desktop-header-h: 72px;
    --content-max: 1180px;

    /* Backward-compatible aliases used by existing components */
    --bg: var(--color-cream);
    --bg-deep: #f3eee6;
    --surface: var(--surface-light);
    --surface-2: #f4efe7;
    --ink: var(--color-ink);
    --ink-soft: var(--text-secondary);
    --teal: var(--color-ink);
    --teal-dark: var(--color-ink);
    --teal-bright: #3a3a3c;
    --mint: #e8eadf;
    --accent: var(--color-yellow);
    --line: var(--border-subtle);
    --line-strong: rgba(15, 15, 16, 0.14);
    --danger: #a33a2c;
    --success: var(--color-sage);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    height: 100%;
    width: 100%;
    font-family: var(--font-body);
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    color: var(--text-primary);
    background: var(--bg);
  }

  .theme-dark {
    --bg: var(--surface-dark);
    --bg-deep: #121212;
    --surface: var(--surface-dark-2);
    --surface-2: #2a2a2a;
    --color-ink: var(--text-on-dark);
    --ink: var(--text-on-dark);
    --ink-soft: var(--text-on-dark-muted);
    --line: var(--border-on-dark);
    --line-strong: rgba(247, 243, 236, 0.2);
    --text-primary: var(--text-on-dark);
    --text-secondary: var(--text-on-dark-muted);
    --surface-light: var(--surface-dark-2);
    --border-subtle: var(--border-on-dark);
    --mint: #2a2a2a;
  }

  .theme-light {
    --bg: var(--color-cream);
    --ink: var(--color-ink);
    --ink-soft: var(--text-secondary);
    --text-on-dark: var(--text-primary);
    --text-on-dark-muted: var(--text-secondary);
    --surface-dark: var(--color-cream);
    --surface-dark-2: var(--surface-2);
    --border-on-dark: var(--border-subtle);
  }

  @media (display-mode: standalone) {
    body {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-display);
    font-weight: 600;
    color: inherit;
    letter-spacing: -0.03em;
  }

  button,
  input,
  select,
  textarea {
    font-family: var(--font-body);
  }

  button {
    cursor: pointer;
    transition: transform var(--duration) var(--ease),
      background var(--duration) var(--ease),
      border-color var(--duration) var(--ease),
      color var(--duration) var(--ease),
      opacity var(--duration) var(--ease);
  }

  button:active:not(:disabled) {
    transform: scale(0.98);
  }

  button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  a {
    color: inherit;
  }

  ::placeholder {
    color: var(--ink-soft);
    opacity: 1;
  }

  img {
    max-width: 100%;
  }

  ::selection {
    background: rgba(255, 198, 41, 0.35);
  }

  :focus-visible {
    outline: 2px solid var(--color-yellow);
    outline-offset: 2px;
  }

  .title {
    text-align: center;
  }

  .btn-primary {
    border: none;
    background: var(--color-yellow);
    color: var(--color-ink-solid);
    border-radius: var(--radius-pill);
    padding: 12px 18px;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.01em;
    min-height: 44px;
  }

  .btn-primary:hover:not(:disabled) {
    filter: brightness(0.97);
  }

  .btn-ink {
    border: none;
    background: var(--ink);
    color: var(--bg);
    border-radius: var(--radius-pill);
    padding: 12px 18px;
    font-weight: 600;
    font-size: 14px;
    min-height: 44px;
  }

  .btn-secondary {
    border: 1px solid var(--line-strong);
    background: transparent;
    color: var(--ink);
    border-radius: var(--radius-pill);
    padding: 12px 18px;
    font-weight: 600;
    font-size: 14px;
    min-height: 44px;
  }

  .btn-secondary:hover:not(:disabled) {
    border-color: var(--color-ink);
  }

  .btn-soft {
    border: 1px solid transparent;
    background: var(--mint);
    color: var(--color-ink);
    border-radius: var(--radius-pill);
    padding: 12px 16px;
    font-weight: 600;
    font-size: 14px;
  }

  .chip {
    border-radius: var(--radius-pill);
    padding: 8px 14px;
    border: 1px solid var(--border-subtle);
    background: var(--surface-light);
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
  }

  .chip.is-active {
    background: var(--color-ink);
    border-color: var(--color-ink);
    color: var(--surface-light);
  }

  .card-surface {
    background: var(--surface-light);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius);
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export default GlobalStyles;
